import React from "react";
import { Avatar, Paper, PaperProps, Stack, Typography } from "@mui/material";

export interface BasicCard extends Omit<PaperProps, "elevation"> {
	avatarSrc?: string;
	/**
	 * @description Main component title
	 */
	title: string;
	/**
	 * @description Alternative text that may show relevant or extra information for the card below the main title
	 */
	subtitle?: string;
	children?: React.ReactNode;
	/**
	 * @description Define weather to show the card with elevation or not
	 */
	elevation?: PaperProps["elevation"];
	/**
	 * @description Color of the border to be used in all corners
	 */
	borderColor?: string;
	/**
	 * @description Border width to be used in all corners. Provide a number that will be represented as pixels
	 */
	borderWidth?: number;
	/**
	 * @description Show the left border
	 */
	leftBorder?: boolean;
	/**
	 * @description Show the top border
	 */
	topBorder?: boolean;
	/**
	 * @description Show the right border
	 */
	rightBorder?: boolean;
	/**
	 * @description Show the bottom border
	 */
	bottomBorder?: boolean;
}

export function BasicCard({
	avatarSrc,
	title,
	subtitle,
	children,
	elevation = 0,
	borderColor = "#1976d2",
	borderWidth = 4,
	leftBorder = false,
	topBorder = false,
	rightBorder = false,
	bottomBorder = false,
	sx,
	...paperProps
}: BasicCard) {
	const borderStyles = {
		borderLeft: leftBorder ? `${borderWidth}px solid ${borderColor}` : undefined,
		borderTop: topBorder ? `${borderWidth}px solid ${borderColor}` : undefined,
		borderRight: rightBorder ? `${borderWidth}px solid ${borderColor}` : undefined,
		borderBottom: bottomBorder ? `${borderWidth}px solid ${borderColor}` : undefined,
	};

	return (
		<Paper
			elevation={elevation}
			sx={{
				p: 2,
				display: "flex",
				flexDirection: "column",
				...borderStyles,
				...sx,
			}}
			{...paperProps}
		>
			<Stack mb={1} direction="row">
				{<Avatar src={avatarSrc} className={"mr-4"} />}
				<Stack>
					<Typography className="!font-montserrat !text-[14.5px] !text-default !font-medium">{title}</Typography>
					{subtitle && <Typography className="!font-sans !text-[14.5px] !text-default">{subtitle}</Typography>}
				</Stack>
			</Stack>

			<Typography className="!font-sans !text-[14.5px] !text-default">{children}</Typography>
		</Paper>
	);
}
